// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import emailPreviewApi from '../api/emailPreviewApi';
import { sendNudge } from '../api/nudgeApi';
import type { EmailPolicyPreview, EmailPreviewResponse } from '../types/emailPreview';
import type { NudgeDataResponse, NudgeRequest, NudgeResult } from '../types/nudge';

/**
 * Surface the backend's human-readable reason (e.g. "No completed LOA found
 * for policy …", "No email address configured for provider …") instead of
 * the generic axios "Request failed with status code 400".
 */
function extractNudgeError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;
}

/**
 * Adapt the unified {@link EmailPreviewResponse} the server returns to the
 * {@link NudgeDataResponse} shape the nudge modal + editor + history
 * components were originally built against. Keeps the UI migration
 * contained: the hook swaps endpoints; downstream components are
 * untouched.
 */
function adaptPreviewToNudgeData(preview: EmailPreviewResponse, policyId: string): NudgeDataResponse {
  const policy = preview.policies[0];
  const recipient = preview.recipients[0];
  const loa = preview.nudgeContext?.loaInfo;
  const history = preview.nudgeContext?.history;
  const counters = preview.rateLimit?.counters ?? {};
  const thresholds = preview.rateLimit?.thresholds ?? {};

  return {
    policyInfo: {
      policyId: policy?.policyId ?? policyId,
      policyNumber: policy?.policyNumber ?? null,
      providerId: recipient?.id ?? '',
      providerName: recipient?.name ?? policy?.providerName ?? '',
      providerEmail: recipient?.email ?? '',
      policyTypeName: policy?.policyTypeName ?? null,
      profileId: '',
      profileName: '',
      hasLoaSlice: false,
    },
    loaInfo: {
      loaId: loa?.loaId ?? '',
      loaReference: loa?.loaReference ?? '',
      status: loa?.status ?? '',
      completedAt: loa?.completedAt ?? '',
      originalNotificationSentAt: loa?.originalNotificationSentAt ?? undefined,
    },
    // Dual-template adapter was removed when LOA provider nudges unified
    // on the customBody-injection pattern (shared with CoA/LOA client
    // invitations). Only one default subject + body now.
    defaultTemplate: {
      subject: preview.subject,
      bodyHtml: preview.editableBodyHtml,
    },
    rateLimitStatus: {
      warningCode: (preview.rateLimit?.code as NudgeDataResponse['rateLimitStatus']['warningCode']) ?? 'NONE',
      warningMessage: preview.rateLimit?.message ?? undefined,
      nudgesForPolicyToday: counters.nudgesForPolicyToday ?? 0,
      nudgesForProviderToday: counters.nudgesForProviderToday ?? 0,
      nudgesForProviderThisWeek: counters.nudgesForProviderThisWeek ?? 0,
      nudgesForProviderThisMonth: counters.nudgesForProviderThisMonth ?? 0,
      totalNudgesForPolicy: counters.totalNudgesForPolicy ?? 0,
      lastNudgeAt: preview.rateLimit?.lastSentAt ?? undefined,
      thresholds: {
        maxPerPolicyPerDay: thresholds.maxPerPolicyPerDay ?? 0,
        maxPerProviderPerDay: thresholds.maxPerProviderPerDay ?? 0,
        maxPerProviderPerWeek: thresholds.maxPerProviderPerWeek ?? 0,
        maxPerProviderPerMonth: thresholds.maxPerProviderPerMonth ?? 0,
        minHoursBetweenNudges: thresholds.minHoursBetweenNudges ?? 0,
      },
    },
    nudgeHistory: {
      policyId,
      totalNudgeCount: history?.totalNudgeCount ?? 0,
      successfulNudgeCount: history?.successfulNudgeCount ?? 0,
      failedNudgeCount: history?.failedNudgeCount ?? 0,
      firstNudgeAt: history?.firstNudgeAt ?? undefined,
      lastNudgeAt: history?.lastNudgeAt ?? undefined,
      entries: (history?.entries ?? []).map((e) => ({
        notificationId: e.notificationId,
        sentAt: e.sentAt ?? e.createdAt,
        sentBy: e.sentBy ?? '',
        recipientEmail: e.recipientEmail,
        subject: e.subject,
        status: e.status,
        hasAttachment: e.hasAttachment,
        errorMessage: e.errorMessage ?? undefined,
        retryCount: e.retryCount,
      })),
    },
  };
}

/**
 * Hook to fetch nudge data for a policy via the unified
 * {@code POST /api/email-preview} endpoint. Preserves the legacy
 * {@link NudgeDataResponse} shape via an in-hook adapter so the nudge
 * modal + editor + history components don't have to change.
 */
export const useNudgeData = (policyId: string, enabled = true) => {
  const [data, setData] = useState<NudgeDataResponse | null>(null);
  // Raw policy rows from the preview response — passed unchanged to
  // ClientPolicyTable so the nudge page renders the exact same table
  // as the invitation + resend pages.
  const [policies, setPolicies] = useState<EmailPolicyPreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !policyId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const preview = await emailPreviewApi.preview({
        kind: 'LOA_PROVIDER_NUDGE',
        policyId,
      });
      setData(adaptPreviewToNudgeData(preview, policyId));
      setPolicies(preview.policies);
    } catch (err) {
      console.error('Failed to fetch nudge data:', err);
      setError(new Error(extractNudgeError(err, 'Failed to fetch nudge data')));
    } finally {
      setIsLoading(false);
    }
  }, [policyId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    policies,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook to send a nudge email
 *
 * Returns a mutation-like interface for consistency.
 */
export const useSendNudge = (policyId: string) => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(async (request: NudgeRequest): Promise<NudgeResult> => {
    setIsPending(true);
    setError(null);

    try {
      const result = await sendNudge(policyId, request);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to send nudge');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  }, [policyId]);

  return {
    mutateAsync,
    isPending,
    error,
  };
};

export default {
  useNudgeData,
  useSendNudge,
};
